import { useTranslation } from 'react-i18next';
import { PanelLeftOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSubNav } from '@/contexts/SubNavContext';

/**
 * Compact pill shown in the top-left of the main content area when the
 * persistent sub-nav panel has been collapsed by the user. Click → re-open.
 *
 * Matches HeyGen's "collapsed module header" pattern (capture 5 in the design
 * brief).
 */
export function RailSubnavCollapsedPill() {
  const { state, activeItem, open } = useSubNav();
  const { t } = useTranslation('navigation');

  if (state !== 'collapsed' || !activeItem) return null;

  const ActiveIcon = activeItem.icon;

  return (
    <button
      type="button"
      onClick={open}
      aria-label={t('expand_subnav', { defaultValue: 'Étendre le sous-menu' })}
      className={cn(
        'group sticky top-3 z-20 inline-flex items-center gap-2 self-start',
        'rounded-full border border-slate-200 bg-white/85 backdrop-blur-md pl-1 pr-2.5 py-1',
        'shadow-[0_4px_12px_-2px_rgba(15,23,42,0.08),0_1px_2px_rgba(15,23,42,0.04)]',
        'transition-all hover:scale-[1.02] hover:shadow-[0_6px_16px_-2px_rgba(15,23,42,0.12)]',
        'supports-[backdrop-filter]:bg-white/70',
        'dark:border-charcoal-700 dark:bg-charcoal-900/85 dark:supports-[backdrop-filter]:bg-charcoal-900/70',
        // Hide on mobile — drawer-based subnav is used there
        'hidden lg:inline-flex',
      )}
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-lime-100 text-lime-900 dark:bg-lime-500/15 dark:text-lime-300">
        <ActiveIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
      </span>
      <span className="font-head text-[12px] font-semibold text-charcoal-950 dark:text-slate-100">
        {t(activeItem.labelKey)}
      </span>
      <PanelLeftOpen
        className="ml-0.5 h-3.5 w-3.5 text-slate-400 transition-colors group-hover:text-charcoal-900 dark:text-slate-500 dark:group-hover:text-slate-200"
        strokeWidth={1.75}
        aria-hidden="true"
      />
    </button>
  );
}
